import React, { useEffect, useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import {
  calculateRiskScore,
  formatDocument,
  formatToReal,
  getLetterScore,
  getScoreLegends,
  maskCEP,
} from "../../../utils/utils";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import NoOcurrence from "../../common/query/NoOcurrence";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AvisoCard from "../../common/query/AvisoCard";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import ReactApexChart from "react-apexcharts";
import ScoreRating from "./ScoreRating";
import { Badge, Modal, Table } from "flowbite-react";
import QuadroSocietario from "../../common/query/QuadroSocietario";
import ParticipacaoEmEmpresas from "../../common/query/ParticipacaoEmEmpresas";
import Socios from "../../common/query/Socios";
import AcoesCiveis from "../../common/query/AcoesCiveis";
import DecisionBanner from "../DecisionBanner";
import StreetView from "../../common/query/StreetView";
import { HiOutlineEye, HiThumbDown, HiThumbUp } from "react-icons/hi";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import moment from "moment";
import FalenciaRecuperacaoJudicial from "../../common/query/FalenciaRecuperacaoJudicial";
import { FaRegChartBar, FaUserCheck } from "react-icons/fa";
import { IoReceiptOutline } from "react-icons/io5";
import { TfiBarChart } from "react-icons/tfi";
import { LuBuilding2 } from "react-icons/lu";
import { BsBank } from "react-icons/bs";
import {
  GrDocumentExcel,
  GrDocumentText,
  GrDocumentTime,
  GrMoney,
} from "react-icons/gr";
import { CiMoneyCheck1 } from "react-icons/ci";
import { MdOutlineBalance } from "react-icons/md";
import { HiOutlineCurrencyDollar } from "react-icons/hi";
import { FiUserCheck, FiUsers } from "react-icons/fi";
import { BiLineChartDown } from "react-icons/bi";

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const formatYYYYMMToYY = (yyyyMM) => {
  if (!yyyyMM) return "–";
  const [y, m] = yyyyMM.split("-");
  return `${m}/${String(y).slice(-2)}`;
};

const firstNonEmptySeries = (src, keys) => {
  for (const k of keys) {
    const arr = src?.[k];
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
};

const DefineNegocio = ({ data, dataFull }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [openMap, setOpenMap] = useState(false);
  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao
  );

  // Fallbacks para novos campos vindos do formatador
  const sociosEmpresa = data?.socios;
  const administradoresEmpresa = data?.administradores;

  const participacoesHierarquico =
    data?.participacoesSociosAdmOutrasEmpresas || [];
  const participacoesEmpresaConsultada =
    dataFull?.participacoesEmpresaConsultada ||
    data?.participacaoEmEmpresas ||
    [];

  const faturamentoPresumido = dataFull?.faturamentoPresumido;

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +data?.score?.score,
        +data?.score?.probabilidade
      )
    );
  }, [data]);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade && data?.dadosCadastrais?.uf
      ? `${data?.dadosCadastrais?.cidade} - ${data?.dadosCadastrais?.uf}`
      : data?.dadosCadastrais?.cidade || data?.dadosCadastrais?.uf,
    maskCEP(data?.dadosCadastrais?.cep),
  ]
    .filter(Boolean)
    .join(", ");

  const compromissosSeries = data?.compromissos?.series ?? [];
  const creditosSeries = data?.creditoObtido?.series ?? [];
  const periodosSeries = [
    ...new Set(
      [
        ...compromissosSeries.map((pt) => pt.mes).filter(Boolean),
        ...creditosSeries.map((pt) => pt.mes).filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCompromissoSerie = (mes) =>
    compromissosSeries.find((pt) => pt.mes === mes);
  const getCreditoSerie = (mes) => creditosSeries.find((pt) => pt.mes === mes);
  const formatCompromissoValor = (valor) => {
    const parsed = parseFloat(valor);
    if (isNaN(parsed)) {
      return "–";
    }
    if (parsed === 0) {
      return "< 10";
    }
    return parsed * 100;
  };
  const formatCreditoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };

  const statusConsumidorContainsNao = data?.statusConsumidor
    ? data?.statusConsumidor.toUpperCase().includes("NÃO") ||
      data?.statusConsumidor.toUpperCase().includes("NAO") ||
      data?.statusConsumidor.toUpperCase().includes("CANCELADA") ||
      data?.statusConsumidor.toUpperCase().includes("SEM")
    : false;

  const rawDemaisInformacoes =
    data?.demaisInformacoes ?? data?.demais_informacoes ?? {};
  const tempoRelacionamentoRaw =
    rawDemaisInformacoes?.tempoRelacionamentoFornecedores ??
    rawDemaisInformacoes?.tempo_relacionamento_fornecedores
      ?.historico_periodo ??
    rawDemaisInformacoes?.tempo_relacionamento_fornecedores ??
    [];
  const tempoRelacionamento = Array.isArray(tempoRelacionamentoRaw)
    ? tempoRelacionamentoRaw
    : [];
  const tempoValores = tempoRelacionamento.map((item) => {
    const valor = Number(item?.valor ?? 0);
    return Number.isFinite(valor) ? valor : 0;
  });
  const maxTempoValor = tempoValores.length > 0 ? Math.max(...tempoValores) : 0;
  const eventosImportantesRaw =
    rawDemaisInformacoes?.acontecimentosImportantes ??
    rawDemaisInformacoes?.acontecimentos_importantes
      ?.historico_periodo_pontuacao ??
    rawDemaisInformacoes?.acontecimentos_importantes ??
    [];
  const eventosImportantes = Array.isArray(eventosImportantesRaw)
    ? eventosImportantesRaw
    : [];
  const hasDemaisInformacoes =
    eventosImportantes.length > 0 || tempoRelacionamento.length > 0;
  const getTempoBarWidth = (valor) => {
    const safeValor = Number.isFinite(Number(valor)) ? Number(valor) : 0;
    if (maxTempoValor <= 0) {
      return 0;
    }
    const percent = (safeValor / maxTempoValor) * 100;
    return Math.min(Math.max(percent, 4), 100);
  };
  const getEventoValor = (evento) => {
    if (evento?.valor != null && evento?.valor !== "") {
      return evento.valor;
    }
    if (evento?.pontuacao != null) {
      return formatToReal(evento.pontuacao);
    }
    return "—";
  };

  return (
    <div>
      <DecisionBanner compact={true} flag={data?.decisao?.flag} />

      <TitleStripe>
        <div className="flex items-center gap-2">
          <LuBuilding2 className="text-blue-700" />{" "}
          <span>Dados Cadastrais</span>
        </div>
      </TitleStripe>
      <div className="mt-4 grid grid-cols-2 gap-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm">
        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">Razão Social</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
            </div>
            <ModalReceitaFederal cnpj={data?.dadosCadastrais?.cnpj} />
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Nome Fantasia</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.nomeFantasia || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Natureza Jurídica
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.naturezaJuridica || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CNPJ/MF</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.cnpj
                ? formatDocument(data?.dadosCadastrais?.cnpj)
                : "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Inscrição Estadual
            </div>
            <div className="font- medium text-gray-900">
              {data?.dadosCadastrais?.inscricaoEstadual || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              CNAE Principal
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.ramoAtividade || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Fundação</div>
            <div className="font-medium text-gray-900 flex flex-wrap items-center gap-2">
              <span>
                {data?.dadosCadastrais?.dataFundacao || "Indisponível"}
              </span>

              {companyAgeLabel && (
                <Badge color="gray" className="text-[11px] font-medium">
                  {companyAgeLabel}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">
              Situação do CNPJ
            </div>
            <div
              className={`inline-block px-2 py-1 text-xs font-semibold rounded-md text-white ${
                (data?.dadosCadastrais?.situacao || "").toLowerCase() ===
                "ativo"
                  ? "bg-green-600"
                  : "bg-red-600"
              }`}
            >
              {data?.dadosCadastrais?.situacao || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Endereço</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.endereco || "Indisponível"}
              {fullAddress && (
                <div
                  className="flex items-center text-xs gap-1 text-blue-600 cursor-pointer"
                  onClick={() => setOpenMap(true)}
                >
                  <span>- Ver fotos do local</span>
                  <HiOutlineEye className="text-lg" />
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Bairro</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.bairro || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Cidade/UF</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.cidade || "Indisponível"} /{" "}
              {data?.dadosCadastrais?.uf || "--"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CEP</div>
            <div className="font-medium text-gray-900">
              {maskCEP(data?.dadosCadastrais?.cep) || "Indisponível"}
            </div>
          </div>
          <div>
            <div className="text-gray-500 text-xs uppercase">
              Capital Inicial
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.capitalInicial
                ? formatToReal(data?.dadosCadastrais?.capitalInicial)
                : "Indisponível"}
            </div>
          </div>
          <div>
            <div className="text-gray-500 text-xs uppercase">
              Capital Social
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.capitalSocial
                ? formatToReal(data?.dadosCadastrais?.capitalSocial)
                : "Indisponível"}
            </div>
          </div>
        </div>
      </div>
      {/* Avaliação preliminar */}
      <TitleStripe className="mb-4">
        <div className="flex items-center gap-2">
          <FaRegChartBar className="text-blue-700" />{" "}
          <span>Avaliação preliminar de crédito</span>
        </div>
      </TitleStripe>

      {data?.score?.score == 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <p className="font-medium">
            Não foi possível calcular o score desta empresa.
          </p>
          <p className="mt-1">
            É necessário verificar as outras informações disponíveis no
            relatório para uma análise completa.
          </p>
        </div>
      ) : (
        <EvaluationScorePJ
          data={data}
          score={data?.score?.score}
          probability={+data?.score?.probabilidade * 100}
          type="CNPJ"
          hasIncome={false}
        />
      )}
      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}
      {data?.statusConsumidor && (
        <div className="mt-5 w-full">
          <div
            className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-4 text-sm shadow-sm ${
              statusConsumidorContainsNao
                ? "border-rose-200 bg-rose-50/70 text-rose-700"
                : "border-emerald-200 bg-emerald-50/70 text-emerald-700"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border ${
                  statusConsumidorContainsNao
                    ? "border-rose-200 bg-rose-100 text-rose-700"
                    : "border-emerald-200 bg-emerald-100 text-emerald-700"
                }`}
              >
                {statusConsumidorContainsNao ? (
                  <HiThumbDown className="h-5 w-5" />
                ) : (
                  <HiThumbUp className="h-5 w-5" />
                )}
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em] opacity-70">
                  Status do consumidor
                </div>
                <div className="text-base font-semibold text-slate-900">
                  {data?.statusConsumidor}
                </div>
              </div>
            </div>
            <div className="hidden rounded-full border border-white/40 bg-white/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] sm:block">
              {statusConsumidorContainsNao ? "Atenção" : "Regular"}
            </div>
          </div>
        </div>
      )}
      {!statusConsumidorContainsNao && (
        <>
          {" "}
          {data?.pagamentoPontual &&
            data?.pagamentoPontual?.series?.length > 0 && (
              <div>
                <TitleStripe className="mb-4">Pagamentos Pontuais</TitleStripe>
                {data?.pagamentoPontual ? (
                  <>
                    <div className="mt-3 grid gap-3">
                      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                          Pontuação
                        </div>
                        <div className="text-lg font-semibold text-slate-900">
                          {data?.pagamentoPontual?.pontuacao ?? "–"}
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                          Pagamentos pontuais por período
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                {data?.pagamentoPontual?.series?.map(
                                  (pt, idx) => (
                                    <th
                                      key={idx}
                                      className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                    >
                                      {formatYYYYMMToYY(pt.mes)}
                                    </th>
                                  )
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-t border-slate-100">
                                {data?.pagamentoPontual?.series?.map(
                                  (pt, idx) => {
                                    const n = parseFloat(pt?.valor);
                                    return (
                                      <td
                                        key={idx}
                                        className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                                      >
                                        {!isNaN(n) ? `${n}%` : "–"}
                                      </td>
                                    );
                                  }
                                )}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                          100%: Pagamentos pontuais
                        </span>
                        <span className="text-slate-300">|</span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-rose-500" />
                          0%: Pagamentos não pontuais
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>
            )}
          {data?.pagamentoAtrasado &&
            data?.pagamentoAtrasado?.atraso_medio_dias?.length > 0 && (
              <div>
                <TitleStripe className="mb-5">
                  Pagamentos em atrasos
                </TitleStripe>

                {data?.pagamentoAtrasado ? (
                  <>
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Pagamentos em atraso por período (Últimos 12 meses)
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                Tipo
                              </th>
                              {firstNonEmptySeries(data?.pagamentoAtrasado, [
                                "dias_6_15",
                                "dias_16_30",
                                "dias_31_60",
                                "mais_60",
                                "atraso_medio_dias",
                              ])?.map((pt, idx) => (
                                <th
                                  key={idx}
                                  className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                                >
                                  {formatYYYYMMToYY(pt.mes)}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-slate-100">
                            {[
                              ["Período 6 a 15 dias", "dias_6_15"],
                              ["Período 16 a 30 dias", "dias_16_30"],
                              ["Período 31 a 60 dias", "dias_31_60"],
                              ["Mais de 60 dias", "mais_60"],
                              ["Atraso médio (dias)", "atraso_medio_dias"],
                            ].map(([label, key], i) => {
                              const series =
                                data?.pagamentoAtrasado?.[key] || [];
                              if (!Array.isArray(series) || series.length === 0)
                                return null;

                              return (
                                <tr key={i}>
                                  <td className="px-3 py-3 text-left text-xs font-medium text-slate-700">
                                    {label}
                                  </td>
                                  {series.map((pt, idx) => {
                                    const n = parseFloat(pt?.valor);
                                    const isDias = key === "atraso_medio_dias";
                                    return (
                                      <td
                                        key={idx}
                                        className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                                      >
                                        {!isNaN(n)
                                          ? isDias
                                            ? `${n}d`
                                            : `${n}%`
                                          : "–"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-rose-500" />
                        100%: Pagamentos em atrasos
                      </span>
                      <span className="text-slate-300">|</span>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        0%: Pagamentos em dia
                      </span>
                    </div>
                  </>
                ) : (
                  <NoOcurrence />
                )}
              </div>
            )}
          {periodosSeries.length > 0 ? (
            <div>
              <TitleStripe className="mb-5">
                Compromissos e valor dos títulos
              </TitleStripe>
              <>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">
                    Indicadores por período (Últimos 12 meses)
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Indicador
                          </th>
                          {periodosSeries.map((periodo) => (
                            <th
                              key={periodo}
                              className="px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500"
                            >
                              {formatYYYYMMToYY(periodo)}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        <tr>
                          <td className="px-3 py-3 text-left text-sm font-medium text-slate-700">
                            Qtd. de títulos
                          </td>
                          {periodosSeries.map((periodo) => (
                            <td
                              key={`compromisso-${periodo}`}
                              className="px-2 py-3 text-center text-sm font-semibold text-slate-700 tabular-nums"
                            >
                              {formatCompromissoValor(
                                getCompromissoSerie(periodo)?.valor
                              )}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-3 py-3 text-left text-sm font-medium text-slate-700">
                            Valor dos títulos
                          </td>
                          {periodosSeries.map((periodo) => (
                            <td
                              key={`credito-${periodo}`}
                              className="px-2 py-3 text-center text-xs font-semibold text-slate-700 tabular-nums"
                            >
                              {formatCreditoValor(
                                getCreditoSerie(periodo)?.valor
                              )}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {compromissosSeries.length > 0 && (
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                    Descrição: Número total de obrigações financeiras
                    registradas no nome do consumidor ou empresa, como boletos,
                    duplicatas, carnês ou contratos de crédito. Indica a
                    quantidade de compromissos ativos.
                  </div>
                )}
              </>
            </div>
          ) : (
            <NoOcurrence />
          )}
          {hasDemaisInformacoes && (
            <div className="mb-6 grid gap-4 grid-cols-2 mt-6">
              {eventosImportantes.length > 0 && (
                <div className="rounded-3xl bg-gray-50 p-6 border border-gray-200">
                  <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.2em] text-gray-800">
                    <span>Acontecimentos importantes</span>
                    <span className="text-[0.55rem]">Registros</span>
                  </div>
                  <div className="mt-6 space-y-4 max-h-72 overflow-y-auto pr-1">
                    {eventosImportantes.map((evento, index) => (
                      <div
                        key={`${evento.indicador ?? "evento"}-${index}`}
                        className="flex items-start justify-between gap-4 rounded-2xl bg-white px-3 py-3 shadow-inner"
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="text-sm font-semibold leading-tight">
                            {evento.indicador || evento.periodo || "—"}
                          </p>
                          {evento.indicador && evento.periodo && (
                            <p className="text-xs text-gray-500">
                              {evento.periodo}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 rounded-full border border-white/60 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
                          {getEventoValor(evento)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {tempoRelacionamento.length > 0 && (
                <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Tempo de relacionamento com fornecedores
                  </div>
                  <div className="mt-5 space-y-4">
                    {tempoRelacionamento.map((item, index) => {
                      const valorNumerico = Number(item?.valor ?? 0);
                      const barWidth = getTempoBarWidth(valorNumerico);
                      return (
                        <div
                          key={`${item?.periodo ?? "tempo"}-${index}`}
                          className="space-y-2"
                        >
                          <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                            <span>{item?.periodo || "—"}</span>
                            <span className="text-[0.65rem] font-medium uppercase tracking-widest text-slate-400">
                              {item?.valor ?? "0"}
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-sky-500 to-indigo-500 transition-all duration-200"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {/* Quadro societário */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <FiUsers className="text-blue-700" />
            <span>Quadro Societário</span>
          </div>
        </TitleStripe>
        {sociosEmpresa?.length > 0 ? (
          <Socios socios={sociosEmpresa} />
        ) : (
          <NoOcurrence />
        )}
      </div>
      {/* Administradores */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <FiUsers className="text-blue-700" />
            <span>Administradores</span>
          </div>
        </TitleStripe>
        {administradoresEmpresa?.length > 0 ? (
          <Socios isAdm={true} socios={administradoresEmpresa} />
        ) : (
          <NoOcurrence />
        )}
      </div>
      {/* Participações hierárquicas: cada sócio/adm e suas empresas */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <LuBuilding2 className="text-blue-700" />
            <span>Participações dos Sócios e Adm. em Outras Empresas</span>
          </div>
        </TitleStripe>
        {participacoesHierarquico?.length > 0 ? (
          <div className="space-y-4">
            {participacoesHierarquico.map((node, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-3 bg-white"
              >
                <div className="text-sm font-semibold">
                  {node?.socio?.nome} — {node?.socio?.cpf}
                </div>
                <div className="text-xs text-gray-500 mb-2">
                  {node?.socio?.cargo || "Sócio/Adm"}
                </div>

                {node?.participacoes?.length ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <Table.Head>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          Empresa
                        </Table.HeadCell>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          CNPJ
                        </Table.HeadCell>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          Participação
                        </Table.HeadCell>
                        <Table.HeadCell className="text-left text-black text-xs px-1">
                          Entrada
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {node.participacoes.map((p, i2) => (
                          <Table.Row key={i2}>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.empresa || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.cnpj ? formatDocument(p?.cnpj) : "—"}
                            </Table.Cell>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.participacaoEmPorcentagem || "—"}
                            </Table.Cell>
                            <Table.Cell className="text-left text-black text-xs px-1">
                              {p?.dataEntrada || "—"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    Sem participações.
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      {/* Faturamento Presumido */}
      {faturamentoPresumido &&
        (faturamentoPresumido?.valor != null ||
          faturamentoPresumido?.series?.length > 0) && (
          <div>
            <TitleStripe className="mb-4">
              <div className="flex items-center gap-2">
                <GrMoney className="text-blue-700" />{" "}
                <span>Faturamento Presumido</span>
              </div>
            </TitleStripe>

            <div className="mt-2 font-semibold">
              Estimado:{" "}
              <Badge className="w-fit inline" color="gray">
                {faturamentoPresumido?.valor != null
                  ? formatToReal(faturamentoPresumido.valor)
                  : "–"}
              </Badge>
            </div>

            {faturamentoPresumido?.series?.length > 0 && (
              <div className="mt-2 w-full rounded-lg border border-gray-300 overflow-auto">
                <Table>
                  <Table.Head>
                    {faturamentoPresumido.series.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row>
                      {faturamentoPresumido.series.map((pt, idx) => (
                        <Table.Cell
                          key={idx}
                          className="text-center text-black text-xs px-1"
                        >
                          {pt?.valor != null ? formatToReal(pt.valor) : "–"}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            )}

            <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
              Estimativa baseada em sinais comportamentais e declarações
              públicas disponíveis para o CNPJ. Use com cautela.
            </div>
          </div>
        )}
      {/* Negativas, protestos, ações, cheques */}
      <div>
        <TitleStripe className="mb-4">
          <div className="flex items-center gap-2">
            <HiOutlineCurrencyDollar className="text-blue-700" />
            <span>Negativações Registradas</span>
          </div>
        </TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <PendenciasFinanceiras
            quantidadeTotal={
              data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
            }
            valorTotal={data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal}
            pendenciasFinanceiras={data?.pendenciasFinanceiras}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentExcel className="text-blue-700" />
            <span>Títulos protestados</span>
          </div>
        </TitleStripe>
        {data?.protestos?.length > 0 ? (
          <Protestos
            quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
            protestos={data?.protestos}
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <MdOutlineBalance className="text-blue-700" />
            <span>Ações Cíveis</span>
          </div>
        </TitleStripe>
        {data?.acoesCiveis?.length > 0 ? (
          <AcoesCiveis
            quantidadeTotal={data?.resumoConsulta?.acoesCiveis?.quantidadeTotal}
            acoesCiveis={data?.acoesCiveis}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <BiLineChartDown className="text-blue-700" />
            <span>Falências/Recuperações Judiciais</span>
          </div>
        </TitleStripe>
        {data?.falenciasRecJudiciais?.length > 0 ? (
          <FalenciaRecuperacaoJudicial
            falenciaRecuperacaoJudicial={data?.falenciasRecJudiciais?.map(
              (fal) => ({ ...fal, tipo_ocorrencia: fal.tipo })
            )}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sem fundo</span>
          </div>
        </TitleStripe>
        {data?.chequesSemFundo?.length > 0 ? (
          <ChequesSemFundos
            quantidadeTotal={
              data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal
            }
            chequesSemFundos={data?.chequesSemFundo}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <CiMoneyCheck1 className="text-blue-700" />
            <span>Cheques sustados</span>
          </div>
        </TitleStripe>
        {data?.chequesSustados?.length > 0 ? (
          <ChequesSustados
            quantidadeTotal={
              data?.resumoConsulta?.chequesSustados?.quantidadeTotal
            }
            chequesSustados={data?.chequesSustados}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>
      {/* Histórico de consultas */}
      <div>
        <TitleStripe className="mb-5">
          <div className="flex items-center gap-2">
            <GrDocumentTime className="text-blue-700" />
            <span>Histórico de consultas</span>
          </div>
        </TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex-1">
              <HistoricoConsultas
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {/* gráfico opcional comentado */}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          {fullAddress ? (
            <StreetView address={fullAddress} height={460} />
          ) : (
            <div className="p-3 text-sm text-gray-500">
              Endereço indisponível.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DefineNegocio;
